import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveJobStore } from '../../store/liveJobStore';

interface LiveEventsViewerProps {
  maxEvents?: number;
  autoScroll?: boolean;
  showFilters?: boolean;
}

export default function LiveEventsViewer({ 
  maxEvents = 100, 
  autoScroll = true,
  showFilters = true 
}: LiveEventsViewerProps) {
  const navigate = useNavigate();
  const { recentEvents, connected, clearAllData } = useLiveJobStore();
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const eventsContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to top when new events arrive
  useEffect(() => {
    if (autoScroll && eventsContainerRef.current && !paused) {
      eventsContainerRef.current.scrollTop = 0;
    }
  }, [recentEvents, autoScroll, paused]);

  // Filter events
  const filteredEvents = recentEvents.filter(event => {
    if (filter === 'all') return true;
    if (filter === 'jobs') return event.type.startsWith('job:');
    if (filter === 'agents') return event.type.startsWith('agent:');
    if (filter === 'schema') return event.type.startsWith('schema:');
    if (filter === 'system') return event.type.startsWith('system:');
    return event.type === filter;
  }).slice(0, maxEvents);

  const clearEvents = () => {
    clearAllData();
  };

  // Debug function to test filters (remove after testing)
  const generateTestEvents = () => {
    const testEvents = [
      {
        type: 'job:created',
        jobId: 'test-job-1',
        jobName: 'DocumentUploadIngestJob',
        message: '🆕 Queued: DocumentUploadIngest for "test-document.pdf"',
        icon: '📄',
        color: 'text-lex-accent',
        data: { isDocumentJob: true }
      },
      {
        type: 'agent:started',
        jobId: 'test-job-2',
        agentName: 'DocumentProcessorAgent',
        message: '🤖 Agent DocumentProcessor started processing TextExtraction',
        icon: '🤖',
        color: 'text-purple-600',
        data: {}
      },
      {
        type: 'schema:validation:failed',
        jobId: 'test-job-3',
        message: '⚠️ Schema validation failed: UserProfile',
        icon: '⚠️',
        color: 'text-orange-600',
        data: {}
      },
      {
        type: 'system:connected',
        message: 'Connected to live event stream',
        icon: '🟢',
        color: 'text-green-600',
        data: { socketId: 'test-socket' }
      }
    ];

    testEvents.forEach((eventData, index) => {
      setTimeout(() => {
        // Manually add to store for testing
        useLiveJobStore.getState().addEvent(eventData);
      }, index * 500); // Stagger events
    });
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    return date.toLocaleTimeString();
  };

  // Handle event click - navigate to job trace or details
  const handleEventClick = (event: any) => {
    if (event.jobId) {
      // Navigate to job trace page
      navigate(`/system-monitor/job-trace/${event.jobId}`);
    }
  };

  // Determine if event is clickable
  const isClickableEvent = (event: any) => {
    return event.jobId && (event.type.startsWith('job:') || event.type.startsWith('agent:'));
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">📡 Live Events</h3>
          <div className={`flex items-center gap-2 px-2 py-1 rounded text-xs font-medium ${
            connected 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
            {connected ? 'Live' : 'Offline'}
          </div>
          <span className="text-sm text-gray-500">({filteredEvents.length} events)</span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPaused(!paused)}
            className={`px-3 py-1 rounded text-xs font-medium transition ${
              paused 
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            {paused ? '▶️ Resume' : '⏸️ Pause'}
          </button>
          <button
            onClick={generateTestEvents}
            className="px-3 py-1 bg-green-100 text-green-800 hover:bg-green-200 rounded text-xs font-medium transition"
          >
            🧪 Test Events
          </button>
          <button
            onClick={clearEvents}
            className="px-3 py-1 bg-red-100 text-red-800 hover:bg-red-200 rounded text-xs font-medium transition"
          >
            🧹 Clear
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'all', label: 'All', icon: '📊' },
              { value: 'jobs', label: 'Jobs', icon: '⚙️' },
              { value: 'agents', label: 'Agents', icon: '🤖' },
              { value: 'schema', label: 'Schema', icon: '📋' },
              { value: 'system', label: 'System', icon: '🔧' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                className={`px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition ${
                  filter === option.value
                    ? 'bg-lex-accent text-lex-accent-contrast'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <span>{option.icon}</span>
                <span>{option.label}</span>
                {filter === option.value && filteredEvents.length > 0 && (
                  <span className="ml-1 bg-white bg-opacity-20 px-1 rounded text-xs">
                    {filteredEvents.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Events List */}
      <div 
        ref={eventsContainerRef}
        className="flex-1 overflow-y-auto p-3 space-y-2"
      >
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <div className="text-4xl mb-2">📡</div>
            <p>No events for filter: <strong>{filter}</strong></p>
            <p className="text-xs mt-1">
              {paused ? 'Events are paused' : connected ? 'Waiting for activity from global store' : 'WebSocket disconnected'}
            </p>
          </div>
        ) : (
          filteredEvents.map(event => {
            const clickable = isClickableEvent(event);
            return (
              <div 
                key={event.id}
                onClick={() => clickable && handleEventClick(event)}
                className={`flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all ${
                  clickable
                    ? 'hover:shadow-md hover:border-lex-accent cursor-pointer hover:bg-lex-state-hover dark:hover:bg-lex-state-hover'
                    : 'hover:shadow-sm'
                }`}
              >
                <div className="text-lg">{event.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${event.color} ${clickable ? 'hover:underline' : ''}`}>
                      {event.message}
                      {clickable && (
                        <span className="ml-2 text-xs text-lex-accent">
                          → View Trace
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTimestamp(event.timestamp)}
                    </span>
                  </div>
                  
                  {(event.jobId || event.agentName) && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 space-x-3">
                      {event.jobId && (
                        <span>Job: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{event.jobId.slice(0, 8)}...</code></span>
                      )}
                      {event.agentName && (
                        <span>Agent: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{event.agentName}</code></span>
                      )}
                      {event.progress !== undefined && (
                        <span>Progress: <strong>{event.progress}%</strong></span>
                      )}
                    </div>
                  )}

                  {event.phase && (
                    <div className="text-xs text-lex-accent mt-1">
                      Phase: {event.phase}
                    </div>
                  )}

                  {/* Show additional context for system events */}
                  {event.type.startsWith('system:') && event.data && (
                    <div className="text-xs text-gray-500 mt-1">
                      {event.data.totalCount && (
                        <span>Total: {event.data.totalCount} jobs</span>
                      )}
                      {event.data.interestingCount && (
                        <span> • Active/Recent: {event.data.interestingCount}</span>
                      )}
                      {event.data.breakdown && (
                        <span> • Active: {event.data.breakdown.active}, Failed: {event.data.breakdown.failed}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
} 